/**
 * Tool Combo Section Component
 *
 * Allows configuring tool combo at the Agent level:
 * - Enable/disable tool combo
 * - Select a global template
 * - Or define a custom combo inline
 */

import React, { useState, useMemo } from "react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import {
  Layers,
  ChevronDown,
  ChevronUp,
  Plus,
  Trash2,
  ExternalLink,
  Info,
  Pencil,
} from "lucide-react";
import { useToolComboTemplates } from "@/features/system-settings/hooks/useSystemConfig";
import { useToolsQuery } from "@/lib/queries";
import { useI18n } from "@/i18n";
import { getConfigValue } from "@/config/fieldConfig";
import type { AgentToolComboConfig, AgentConfiguration } from "@/types";
import { EditToolComboTemplateDrawer } from "./EditToolComboTemplateDrawer";

interface ToolComboSectionProps {
  config: Partial<AgentConfiguration>;
  onChange: (name: string, value: any) => void;
}

export const ToolComboSection: React.FC<ToolComboSectionProps> = ({
  config,
  onChange,
}) => {
  const { language } = useI18n();
  const isChinese = language === "zh";

  // Check if skills are enabled (classifier enabled + skills_enabled)
  const classifierEnabled =
    getConfigValue(config, "hooks.classifier.enabled") ?? false;
  const skillsEnabled =
    getConfigValue(config, "hooks.classifier.skills_enabled") ?? false;
  const isSkillsActive = classifierEnabled && skillsEnabled;

  // Translations - dynamic based on skills status
  const t = {
    // Title and description change based on skills status
    title: isSkillsActive
      ? isChinese
        ? "工具组合（后备）"
        : "Tool Combo (Fallback)"
      : isChinese
        ? "工具组合"
        : "Tool Combo",
    description: isSkillsActive
      ? isChinese
        ? "当 Skill 未指定工具组合时，使用此配置"
        : "Used when Skills do not specify a tool combo"
      : isChinese
        ? "配置工具使用顺序和指引"
        : "Configure tool usage order and guidance",
    enabled: isChinese ? "启用工具组合" : "Enable Tool Combo",
    enabledDesc: isChinese
      ? "启用后，工具组合将注入到系统提示词中"
      : "When enabled, tool combo will be injected into system prompt",
    mode: isChinese ? "配置模式" : "Configuration Mode",
    useTemplate: isChinese ? "使用模板" : "Use Template",
    useCustom: isChinese ? "自定义配置" : "Custom Configuration",
    selectTemplate: isChinese ? "选择模板" : "Select Template",
    noTemplate: isChinese ? "无 (不使用模板)" : "None (no template)",
    noTemplates: isChinese
      ? "暂无可用模板，请先在系统设置中创建"
      : "No templates available. Create one in System Settings first.",
    manageTemplates: isChinese ? "管理模板" : "Manage Templates",
    editTemplate: isChinese ? "编辑此模板" : "Edit this template",
    customTools: isChinese ? "工具列表" : "Tools",
    addTool: isChinese ? "添加工具" : "Add Tool",
    toolName: isChinese ? "工具名称" : "Tool Name",
    selectTool: isChinese ? "选择工具" : "Select Tool",
    usageInstruction: isChinese ? "使用说明" : "Usage Instruction",
    guidance: isChinese ? "整体指引" : "Overall Guidance",
    guidancePlaceholder: isChinese
      ? "使用这些工具的整体指导说明..."
      : "Overall guidance for using these tools...",
    noTools: isChinese ? "还没有添加工具" : "No tools added yet",
    // Fallback info (only shown when skills are active)
    priority: isChinese ? "作为后备配置" : "Fallback Configuration",
    priorityDesc: isChinese
      ? "此工具组合仅在以下情况生效：用户消息没有命中任何带有 tool_combo_id 的 Skill 时。如果有 Skill 指定了工具组合，该 Skill 的工具组合会直接内嵌到技能指令中，此处的配置将被忽略。"
      : "This tool combo only takes effect when: user message does not match any Skill with tool_combo_id. If a Skill specifies a tool combo, it will be embedded directly into the skill instructions, and this configuration will be ignored.",
    priorityOrder: isChinese ? "优先级顺序" : "Priority Order",
    priorityOrderDesc: isChinese
      ? "1. Skill 的 tool_combo_id（最高优先级，内嵌到技能指令）\n2. Agent 的使用模板（此配置）\n3. Agent 的自定义配置（此配置）"
      : "1. Skill tool_combo_id (highest priority, embedded in skill instructions)\n2. Agent use_template (this config)\n3. Agent custom_combo (this config)",
    // Direct usage info (shown when skills are NOT active)
    directUsage: isChinese ? "直接生效" : "Direct Usage",
    directUsageDesc: isChinese
      ? "当前 Skill 功能未启用，此工具组合将直接注入到系统提示词中。"
      : "Skills are currently disabled. This tool combo will be directly injected into the system prompt.",
  };

  // Get tool combo config from agent config
  const toolCombo = config.tool_combo || { enabled: false };

  // Mode: 'template' or 'custom'
  const [mode, setMode] = useState<"template" | "custom">(
    toolCombo.use_template
      ? "template"
      : toolCombo.custom_combo
        ? "custom"
        : "template",
  );

  // Edit-template drawer state
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(
    null,
  );

  // Fetch available templates
  const { data: templates, isLoading: templatesLoading } =
    useToolComboTemplates(true, isChinese ? "cn" : "en");

  // Fetch available tools
  const { data: toolsData } = useToolsQuery();

  // Get all available tool names
  const availableTools = useMemo(() => {
    if (!toolsData) return [];
    const tools: { name: string; description: string }[] = [];

    toolsData.builtin_toolkits?.forEach((toolkit) => {
      tools.push({
        name: toolkit.name.replace("builtin:", ""),
        description: toolkit.description,
      });
    });

    toolsData.custom_tools?.forEach((tool) => {
      tools.push({
        name: tool.name,
        description: tool.description,
      });
    });

    return tools.sort((a, b) => a.name.localeCompare(b.name));
  }, [toolsData]);

  // Update tool combo config
  const updateToolCombo = (updates: Partial<AgentToolComboConfig>) => {
    onChange("tool_combo", { ...toolCombo, ...updates });
  };

  // Handle mode change
  const handleModeChange = (newMode: "template" | "custom") => {
    setMode(newMode);
    if (newMode === "template") {
      // Clear custom combo when switching to template mode
      updateToolCombo({ custom_combo: null });
    } else {
      // Clear template when switching to custom mode
      updateToolCombo({ use_template: null });
    }
  };

  // Handle template selection
  const handleTemplateChange = (templateId: string | null) => {
    updateToolCombo({ use_template: templateId });
  };

  // Custom combo helpers
  const customCombo = toolCombo.custom_combo || { tools: [], guidance: "" };

  const addCustomTool = () => {
    const maxOrder = Math.max(0, ...customCombo.tools.map((t) => t.order));
    const newTools = [
      ...customCombo.tools,
      { tool_name: "", order: maxOrder + 1, usage_instruction: "" },
    ];
    updateToolCombo({ custom_combo: { ...customCombo, tools: newTools } });
  };

  const updateCustomTool = (index: number, field: string, value: any) => {
    const newTools = [...customCombo.tools];
    newTools[index] = { ...newTools[index], [field]: value };
    updateToolCombo({ custom_combo: { ...customCombo, tools: newTools } });
  };

  const removeCustomTool = (index: number) => {
    const newTools = customCombo.tools.filter((_, i) => i !== index);
    // Re-order
    newTools.forEach((tool, i) => {
      tool.order = i + 1;
    });
    updateToolCombo({ custom_combo: { ...customCombo, tools: newTools } });
  };

  const moveCustomTool = (index: number, direction: "up" | "down") => {
    const newTools = [...customCombo.tools];
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newTools.length) return;

    [newTools[index], newTools[targetIndex]] = [
      newTools[targetIndex],
      newTools[index],
    ];
    newTools.forEach((tool, i) => {
      tool.order = i + 1;
    });
    updateToolCombo({ custom_combo: { ...customCombo, tools: newTools } });
  };

  const updateGuidance = (guidance: string) => {
    updateToolCombo({ custom_combo: { ...customCombo, guidance } });
  };

  return (
    <Card className="p-4">
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Layers className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="text-sm font-medium text-gray-900">{t.title}</h3>
              <p className="text-xs text-gray-500">{t.description}</p>
            </div>
          </div>
          <a
            href="/system-settings"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
          >
            {t.manageTemplates}
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>

        {/* Enable Toggle */}
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div>
            <span className="text-sm font-medium text-gray-700">
              {t.enabled}
            </span>
            <p className="text-xs text-gray-500 mt-0.5">{t.enabledDesc}</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={toolCombo.enabled || false}
              onChange={(e) => updateToolCombo({ enabled: e.target.checked })}
              className="sr-only peer"
            />
            <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>

        {/* Configuration (only show when enabled) */}
        {toolCombo.enabled && (
          <div className="space-y-4 pt-2">
            {/* Mode Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t.mode}
              </label>
              <div className="flex gap-2">
                <button
                  onClick={() => handleModeChange("template")}
                  className={`flex-1 px-3 py-2 text-sm rounded-lg border transition-colors ${
                    mode === "template"
                      ? "bg-blue-50 border-blue-300 text-blue-700"
                      : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {t.useTemplate}
                </button>
                <button
                  onClick={() => handleModeChange("custom")}
                  className={`flex-1 px-3 py-2 text-sm rounded-lg border transition-colors ${
                    mode === "custom"
                      ? "bg-blue-50 border-blue-300 text-blue-700"
                      : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {t.useCustom}
                </button>
              </div>
            </div>

            {/* Template Mode */}
            {mode === "template" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t.selectTemplate}
                </label>
                {templatesLoading ? (
                  <div className="text-sm text-gray-500">Loading...</div>
                ) : templates && templates.length > 0 ? (
                  <div className="flex items-stretch gap-2">
                    <select
                      value={toolCombo.use_template || ""}
                      onChange={(e) =>
                        handleTemplateChange(e.target.value || null)
                      }
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md"
                    >
                      <option value="">{t.noTemplate}</option>
                      {templates.map((tpl) => (
                        <option key={tpl.id} value={tpl.id}>
                          {tpl.name} ({tpl.tool_count} tools)
                        </option>
                      ))}
                    </select>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() =>
                        toolCombo.use_template &&
                        setEditingTemplateId(toolCombo.use_template)
                      }
                      disabled={!toolCombo.use_template}
                      title={t.editTemplate}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 p-3 bg-gray-50 rounded-lg">
                    {t.noTemplates}
                  </div>
                )}
              </div>
            )}

            {/* Custom Mode */}
            {mode === "custom" && (
              <div className="space-y-4">
                {/* Tools */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {t.customTools}
                    </label>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={addCustomTool}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      {t.addTool}
                    </Button>
                  </div>

                  {customCombo.tools.length === 0 ? (
                    <div className="text-center text-gray-400 py-4 border border-dashed rounded-lg text-sm">
                      {t.noTools}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {customCombo.tools.map((tool, index) => (
                        <div
                          key={index}
                          className="bg-gray-50 rounded-lg p-3 border"
                        >
                          <div className="flex items-start gap-2">
                            {/* Order controls */}
                            <div className="flex flex-col gap-0.5">
                              <button
                                onClick={() => moveCustomTool(index, "up")}
                                disabled={index === 0}
                                className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                              >
                                <ChevronUp className="w-4 h-4" />
                              </button>
                              <span className="text-xs text-gray-500 text-center">
                                {tool.order}
                              </span>
                              <button
                                onClick={() => moveCustomTool(index, "down")}
                                disabled={
                                  index === customCombo.tools.length - 1
                                }
                                className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                              >
                                <ChevronDown className="w-4 h-4" />
                              </button>
                            </div>

                            {/* Tool content */}
                            <div className="flex-1 space-y-2">
                              <select
                                value={tool.tool_name}
                                onChange={(e) =>
                                  updateCustomTool(
                                    index,
                                    "tool_name",
                                    e.target.value,
                                  )
                                }
                                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md"
                              >
                                <option value="">{t.selectTool}</option>
                                {availableTools.map((t) => (
                                  <option key={t.name} value={t.name}>
                                    {t.name}
                                  </option>
                                ))}
                              </select>
                              <textarea
                                value={tool.usage_instruction || ""}
                                onChange={(e) =>
                                  updateCustomTool(
                                    index,
                                    "usage_instruction",
                                    e.target.value,
                                  )
                                }
                                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md resize-y min-h-[60px]"
                                rows={3}
                                placeholder={t.usageInstruction}
                              />
                            </div>

                            {/* Remove button */}
                            <button
                              onClick={() => removeCustomTool(index)}
                              className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Guidance */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t.guidance}
                  </label>
                  <textarea
                    value={customCombo.guidance || ""}
                    onChange={(e) => updateGuidance(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md resize-y min-h-[80px]"
                    rows={4}
                    placeholder={t.guidancePlaceholder}
                  />
                </div>
              </div>
            )}

            {/* Info Note - changes based on skills status */}
            {isSkillsActive ? (
              // When skills are enabled: show fallback explanation
              <div className="p-3 bg-blue-50 rounded-lg space-y-3">
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="text-xs font-medium text-blue-700">
                      {t.priority}
                    </span>
                    <p className="text-xs text-blue-600 mt-0.5">
                      {t.priorityDesc}
                    </p>
                  </div>
                </div>
                <div className="pl-6 border-l-2 border-blue-200">
                  <span className="text-xs font-medium text-blue-700">
                    {t.priorityOrder}
                  </span>
                  <p className="text-xs text-blue-600 mt-0.5 whitespace-pre-line">
                    {t.priorityOrderDesc}
                  </p>
                </div>
              </div>
            ) : (
              // When skills are disabled: show direct usage note
              <div className="flex items-start gap-2 p-3 bg-green-50 rounded-lg">
                <Info className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="text-xs font-medium text-green-700">
                    {t.directUsage}
                  </span>
                  <p className="text-xs text-green-600 mt-0.5">
                    {t.directUsageDesc}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <EditToolComboTemplateDrawer
        templateId={editingTemplateId}
        open={editingTemplateId !== null}
        onClose={() => setEditingTemplateId(null)}
      />
    </Card>
  );
};

export default ToolComboSection;
